import React, { useEffect, useState } from 'react';
import Swal from './sweetalert2';
import styled from 'styled-components';
import { FaTrashAlt, FaEdit, FaPlus, FaCheck, FaTimes } from 'react-icons/fa';

const PageContainer = styled.div`
  padding: 40px;
  min-height: 100vh;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
`;

const AddButton = styled.button`
  background: #34574A;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 24px;
  font-size: 1rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  transition: background 0.2s;
  &:hover {
    background: #27443a;
  }
`;

const TableWrapper = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  th, td {
    padding: 16px 12px;
    text-align: left;
    font-size: 0.95rem;
    border-bottom: 1px solid #eee;
  }

  th {
    background: #f8f9fa;
    color: #666;
    font-weight: 600;
    text-transform: uppercase;
  }

  tr:hover {
    background: #f8f9fa;
  }
`;

const StatusButton = styled.button`
  background: ${props => props.ativo ? '#22c55e' : '#c62828'};
  color: #fff;
  border: none;
  border-radius: 12px;
  font-size: 0.85rem;
  font-weight: 500;
  padding: 4px 12px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: background 0.2s;
  &:hover {
    opacity: 0.8;
  }
`;
 
const ActionCell = styled.td`
  display: flex;
  gap: 10px;
  align-items: center;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  color: #c0843d;
  font-size: 1.1rem;
  transition: background 0.2s;
  &:hover {
    background: #f8f8f8;
    color: #c62828;
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin: 24px 0;
`;

const PageButton = styled.button`
  padding: 6px 12px;
  border-radius: 6px;
  border: 1.5px solid ${props => props.active ? '#34574A' : '#eee'};
  background: ${props => props.active ? '#34574A' : '#fff'};
  color: ${props => props.active ? '#fff' : '#222'};
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, color 0.2s;
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
// ===================== COMPONENTE PRINCIPAL =====================
const NewsletterDashboard = () => {
  // URL base da API (usando variável de ambiente VITE_URL_API)
  const API_URL = `${import.meta.env.VITE_URL_API}/newsletter`;

  // Estados
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [editingItem, setEditingItem] = useState(null);

  // ===================== FUNÇÕES LÓGICAS =====================

  // Buscar dados
  const fetchData = async (pg = 1) => {
    setLoading(true);
    setError(null);
    const token = localStorage.getItem('auth_token');
    try {
      const res = await fetch(`${API_URL}?page=${pg}`, {
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      const json = await res.json();
      if (Array.isArray(json)) {
        setData(json);
        setLastPage(1);
      } else {
        setData(json.data || []);
        setLastPage(json.last_page || 1);
      }
    } catch (err) {
      setError('Erro ao carregar dados.');
    }
    setLoading(false);
  };

  // Alternar status
  const handleToggleStatus = async (item) => {
    const novoStatus = item.statusNEWSLETTER === 1 ? 0 : 1;
    const token = localStorage.getItem('auth_token');
    try {
      const res = await fetch(`${API_URL}/${item.codigoNEWSLETTER}`, {
        method: 'PATCH',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ statusNEWSLETTER: novoStatus })
      });
      if (!res.ok) {
        const errorText = await res.text();
        Swal.fire({ icon: 'error', title: 'Erro ao atualizar status', text: errorText });
        return;
      }
      fetchData(page);
    } catch (e) {
      Swal.fire({ icon: 'error', title: 'Erro ao atualizar status', text: e.message });
    }
  };

  // Deletar inscrição
  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: 'Tem certeza?',
      text: 'Deseja realmente excluir esta inscrição? Esta ação não poderá ser desfeita!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#c62828',
      cancelButtonColor: '#34574A',
      confirmButtonText: 'Sim, excluir',
      cancelButtonText: 'Cancelar',
      reverseButtons: true
    });
    if (!result.isConfirmed) return;
    try {
      await fetch(`${API_URL}/${id}`, { 
        method: 'DELETE',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
        }
      });
      fetchData(page);
    } catch {
      Swal.fire({ icon: 'error', title: 'Erro', text: 'Não foi possível excluir a inscrição.' });
    }
  };
 

  const handleEdit = (item) => {
    setEditingItem(item);
    setNewEmail(item.emailNEWSLETTER);
    setShowModal(true);
  };

  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line
  }, [page]);

  const filteredData = data.filter(item =>
    (item.emailNEWSLETTER || '').toLowerCase().includes(search.toLowerCase())
  );

  return (
    <PageContainer>
      {showSuccess && (
        <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',zIndex:2000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#43d477',color:'#fff',padding:'32px 48px',borderRadius:16,boxShadow:'0 6px 32px #0004',fontSize:22,fontWeight:700,display:'flex',alignItems:'center',gap:14,cursor:'pointer'}}>
            <i className="fas fa-check-circle" style={{fontSize:28}}></i>
            Inscrição realizada com sucesso!
          </div>
        </div>
      )}
      {showModal && (
        <div style={{position:'fixed',top:0,left:0,width:'100vw',height:'100vh',background:'#0006',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center'}}>
          <div style={{background:'#fff',padding:36,borderRadius:14,minWidth:350,boxShadow:'0 4px 32px #0002',display:'flex',flexDirection:'column',gap:18}}>
            <h3 style={{margin:0,fontWeight:700,fontSize:20,color:'#34574A'}}>{editingItem ? 'Editar inscrição' : 'Nova inscrição'}</h3>
            <input
              type="email"
              placeholder="E-mail"
              value={newEmail}
              onChange={e=>setNewEmail(e.target.value)}
              style={{padding:'12px',border:'1.2px solid #e0e0e0',borderRadius:8,fontSize:15,outline:'none'}}
              autoFocus
            />
            {modalError && <div style={{color:'#d32f2f',fontWeight:500,fontSize:15}}>{modalError}</div>}
            <div style={{display:'flex',justifyContent:'flex-end',gap:10,marginTop:6}}>
              <button className="submit-button" style={{background:'#bbb',color:'#fff',width:100}} onClick={()=>{setShowModal(false);setEditingItem(null);}} disabled={saving}>Cancelar</button>
              <button className="submit-button" style={{background:'#34574A',color:'#fff',width:120}} disabled={saving} onClick={async()=>{
                setSaving(true);
                setModalError('');
                if(!newEmail.match(/^\S+@\S+\.\S+$/)){
                  setModalError('Digite um e-mail válido.');setSaving(false);return;
                }
                try{
                  let res;
                  if(editingItem){
                    res = await fetch(`${API_URL}/${editingItem.codigoNEWSLETTER}`,{
                      method:'PATCH',
                      headers:{
                        'Content-Type':'application/json',
                        'accept': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                      },
                      body:JSON.stringify({
                        emailNEWSLETTER: newEmail
                      })
                    });
                  } else {
                    res = await fetch(API_URL,{
                      method:'POST',
                      headers:{
                        'Content-Type':'application/json',
                        'accept': 'application/json',
                        'Authorization': `Bearer ${localStorage.getItem('auth_token')}`
                      },
                      body:JSON.stringify({
                        emailNEWSLETTER: newEmail,
                        statusNEWSLETTER: 1
                      })
                    });
                  }
                  if(res.ok){
                    setShowModal(false);
                    setEditingItem(null);
                    setShowSuccess(true);
                    setSearch('');
                    setTimeout(()=>setShowSuccess(false), 3000);
                    fetchData(1);
                  }else{
                    setModalError(editingItem ? 'Erro ao atualizar inscrição.' : 'Erro ao cadastrar.');
                  }
                }catch{
                  setModalError(editingItem ? 'Erro ao atualizar inscrição.' : 'Erro ao cadastrar.');
                }
                setSaving(false);
              }}>Salvar</button>
            </div>
          </div>
        </div>
      )}
      <HeaderRow>
        <h2>Newsletter</h2>
        <AddButton onClick={()=>{setShowModal(true);setNewEmail('');setModalError('');setEditingItem(null);}}>
          <FaPlus />
          Nova inscrição
        </AddButton>
      </HeaderRow>
      <div className="dashboard-search">
        <input
          type="text"
          placeholder="Buscar por e-mail..."
          value={search}
          onChange={e=>setSearch(e.target.value)}
        />
      </div>
      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <th>ID</th>
              <th>E-MAIL</th>
              <th>DATA</th>
              <th>STATUS</th>
              <th>AÇÕES</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5}>Carregando...</td></tr>
            ) : error ? (
              <tr><td colSpan={5}>{error}</td></tr>
            ) : data.length === 0 ? (
              <tr><td colSpan={5}>Nenhuma inscrição encontrada.</td></tr>
            ) : filteredData.map((item, idx) => (
              <tr key={item.codigoNEWSLETTER}>
                <td>{item.codigoNEWSLETTER}</td>
                <td>{item.emailNEWSLETTER}</td>
                <td>{item.dataINSCRICAO ? new Date(item.dataINSCRICAO).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : ''}</td>
                <td>
  <StatusButton
    ativo={item.statusNEWSLETTER === 1}
    onClick={() => handleToggleStatus(item)}
    title={item.statusNEWSLETTER === 1 ? 'Desativar' : 'Ativar'}
  >
    {item.statusNEWSLETTER === 1 ? <FaCheck /> : <FaTimes />} {item.statusNEWSLETTER === 1 ? 'Ativo' : 'Inativo'}
  </StatusButton>
</td>
                <ActionCell>
                  <IconButton onClick={() => handleEdit(item)}>
                    <FaEdit />
                  </IconButton>
                  <IconButton onClick={()=>handleDelete(item.codigoNEWSLETTER)}>
                    <FaTrashAlt />
                  </IconButton>
                </ActionCell>
              </tr>
            ))}
          </tbody>
        </Table>
      </TableWrapper>
      <PaginationWrapper>
        <PageButton disabled={page===1} onClick={()=>setPage(page-1)}>
          Anterior
        </PageButton>
        {[...Array(lastPage).keys()].map((pg, idx) => (
          <PageButton key={idx} active={pg+1===page} onClick={()=>setPage(pg+1)}>
            {pg+1}
          </PageButton>
        ))}
        <PageButton disabled={page===lastPage} onClick={()=>setPage(page+1)}>
          Próxima
        </PageButton>
      </PaginationWrapper>
    </PageContainer>
  );
};

export default NewsletterDashboard;
